import ReactMarkdown from "react-markdown";
import { twMerge } from "tailwind-merge";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { ChatMessage } from "@hooks/chat";
import { Star04, Stars02 } from "untitledui-js/react";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

type AiMessageProps = {
  messages: ChatMessage[];
  isLoading?: boolean;
  className?: string;
};

const LOADING_PHASES = {
  LOADING: "loading",
  PENDING: "pending",
  LOADED: "loaded",
} as const;

const USER_TYPES = {
  USER: "user",
  ASSISTANT: "assistant",
};

const AI_FEEDBACK_FORM = "https://forms.gle/3eKioKWSoaWhnxvz6";

// Typewriter animation settings
const CHARS_PER_FRAME = 4;
const ANIMATION_INTERVAL_MS = 16;

export const AiMessage = ({
  messages,
  className = "",
  isLoading = false,
}: AiMessageProps) => {
  const { t } = useTranslation();
  const [loadingPhase, setLoadingPhase] = useState<
    (typeof LOADING_PHASES)[keyof typeof LOADING_PHASES]
  >(LOADING_PHASES.LOADED);
  const [isVisible, setIsVisible] = useState(false);

  // Typewriter animation state
  const [displayedContent, setDisplayedContent] = useState("");
  const animationRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const targetContentRef = useRef<string>("");

  // Derived state
  const usersFirstMessage = messages.find(
    (message) => message.role === USER_TYPES.USER
  )?.content;
  const agentFirstMessage = messages.find(
    (message) => message.role === USER_TYPES.ASSISTANT
  )?.content;
  const hasMessages = Boolean(usersFirstMessage ?? agentFirstMessage);

  // Check if we have streaming content
  const hasStreamingContent = Boolean(agentFirstMessage);

  // Typewriter animation effect
  useEffect(() => {
    // Update target content when new content arrives
    targetContentRef.current = agentFirstMessage || "";

    // If we're loading (streaming) and have content to animate
    if (isLoading && agentFirstMessage) {
      const animate = () => {
        setDisplayedContent((prev) => {
          const target = targetContentRef.current;
          if (prev.length < target.length) {
            // Add multiple characters per frame for smoother feel
            const nextLength = Math.min(
              prev.length + CHARS_PER_FRAME,
              target.length
            );
            return target.slice(0, nextLength);
          }
          return prev;
        });

        // Keep animation running while streaming to catch new content
        // The loop will be cleaned up when isLoading becomes false
        animationRef.current = globalThis.setTimeout(
          animate,
          ANIMATION_INTERVAL_MS
        );
      };

      // Start animation
      animate();

      return () => {
        if (animationRef.current) {
          clearTimeout(animationRef.current);
          animationRef.current = null;
        }
      };
    } else if (!isLoading && agentFirstMessage) {
      // When loading completes, show full content immediately
      setDisplayedContent(agentFirstMessage);
    } else if (!agentFirstMessage) {
      // Reset when there's no content
      setDisplayedContent("");
    }
  }, [isLoading, agentFirstMessage]);

  // Content to display - use animated content during streaming, full content otherwise
  const contentToDisplay = isLoading
    ? displayedContent
    : agentFirstMessage || "";

  // Enhanced animation logic for refined loading phases
  useEffect(() => {
    if (isLoading && !hasStreamingContent) {
      // Reset to loading phase when loading starts and no content yet
      setLoadingPhase(LOADING_PHASES.LOADING);
      setIsVisible(false);
    } else if (isLoading && hasStreamingContent) {
      // We're streaming and have content - show it immediately
      if (loadingPhase !== LOADING_PHASES.LOADED) {
        setLoadingPhase(LOADING_PHASES.LOADED);
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }
    } else if (hasMessages) {
      // When loading stops and we have messages, start transition
      if (loadingPhase === LOADING_PHASES.LOADING) {
        setLoadingPhase(LOADING_PHASES.PENDING);

        // Brief delay to ensure skeleton is hidden before content appears
        setTimeout(() => {
          setLoadingPhase(LOADING_PHASES.LOADED);
          // Use requestAnimationFrame to ensure DOM is updated before triggering animation
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
        }, 150); // Brief pause between skeleton hide and content show
      } else if (loadingPhase === LOADING_PHASES.LOADED) {
        // If already loaded, ensure visibility is set with proper timing
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }
    } else {
      // No messages and not loading - stay in loaded state but invisible
      setLoadingPhase(LOADING_PHASES.LOADED);
      setIsVisible(false);
    }
  }, [isLoading, hasMessages, hasStreamingContent, loadingPhase]);

  // Loading phase - show skeleton UI
  if (isLoading && loadingPhase === LOADING_PHASES.LOADING) {
    return (
      <div className="space-y-8" data-testid="ai-message-loading">
        <div className="h-9 w-full animate-pulse rounded bg-tertiary-300 dark:bg-tertiary-700" />
        <div className="flex items-center gap-2">
          <Star04 width={16} height={16} className="text-primary-500" />
          <Typography className="text-sm font-normal text-tertiary-300">
            {t("SearchResults.generating")}
          </Typography>
        </div>
        {/* Create an Array with n items and use to create n loading placeholders */}
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={`loading-placeholder-${index}`} className="mb-4">
            <div className="h-3 animate-pulse rounded bg-tertiary-400 dark:bg-tertiary-700" />
          </div>
        ))}
        <div className="h-3 animate-pulse rounded bg-tertiary-400 lg:w-1/2 dark:bg-tertiary-700" />
      </div>
    );
  }

  // Pending phase - brief moment between skeleton hide and content show
  if (loadingPhase === LOADING_PHASES.PENDING) {
    return null;
  }

  // Return null if no messages to display
  if (!hasMessages) {
    return null;
  }

  // Loaded phase - content with enhanced fade-in and move-up animation
  return (
    <div
      className={twMerge(
        "space-y-8 transition-all duration-700 ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
        className
      )}
      data-testid="ai-message-content"
    >
      {usersFirstMessage && (
        <Typography as="h1" size="3xl" className="text-white" data-hj-suppress>
          {usersFirstMessage}
        </Typography>
      )}
      <div
        className="flex items-center gap-2"
        data-testid="ai-message-disclaimer"
      >
        <Stars02 width={16} height={16} className="text-primary-500" />
        <Typography className="text-sm font-normal text-tertiary-300">
          <Trans
            i18nKey="SearchResults.disclaimer"
            components={{
              1: (
                <a
                  href={AI_FEEDBACK_FORM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer text-sm font-semibold underline"
                >
                  {t("SearchResults.disclaimer")}
                </a>
              ),
            }}
          />
        </Typography>
      </div>
      {contentToDisplay && (
        <div
          className="richText aiSearch lg:text-lg [&_p:not(:last-child)]:mb-3 lg:[&_p:not(:last-child)]:mb-5"
          data-testid="ai-message-agent-content"
          data-hj-suppress
          aria-live="polite"
        >
          <ReactMarkdown>{contentToDisplay}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

AiMessage.displayName = "AiMessage";
